export interface CorridorI18nConfig {
  code: string;
  label: string;
  flag: string;
  routePrefix: string;
  legacyRoutePrefix?: string;
  routeSlugs: readonly string[];
  targetCurrency: string;
  ogLocale: string;
  homeSeo: {
    title: string;
    description: string;
  };
}

export interface SupportedLanguage {
  code: string;
  label: string;
  flag: string;
}

export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_TARGET_CURRENCY = 'INR';

export const CORRIDOR_I18N_CONFIG: readonly CorridorI18nConfig[] = [
  {
    code: 'en',
    label: 'English',
    flag: '🇺🇸',
    routePrefix: '/en',
    routeSlugs: ['en'],
    targetCurrency: 'INR',
    ogLocale: 'en_US',
    homeSeo: {
      title: 'Fast and Secure Money Transfers to India at Google FX Rates',
      description:
        'Transfer money to India instantly with real-time Google FX rates and no hidden fees. Enjoy secure, hassle-free transfers with 24/7 support. Launch offer: Signup now.',
    },
  },
  {
    code: 'vi',
    label: 'Tiếng Việt',
    flag: '🇻🇳',
    routePrefix: '/vi',
    legacyRoutePrefix: '/vnd',
    routeSlugs: ['vi'],
    targetCurrency: 'VND',
    ogLocale: 'vi_VN',
    homeSeo: {
      title:
        'Chuyển tiền về Việt Nam nhanh chóng, an toàn với tỷ giá Google FX',
      description:
        'Gửi tiền về Việt Nam ngay lập tức với tỷ giá Google FX theo thời gian thực, không phí ẩn.Trải nghiệm chuyển tiền đơn giản, an toàn cùng hỗ trợ 24/7. Đăng ký ngay và nhận $10 tiền thưởng khi đăng ký!',
    },
  },
  {
    code: 'fil',
    label: 'Filipino',
    flag: '🇵🇭',
    routePrefix: '/fil',
    legacyRoutePrefix: '/php',
    routeSlugs: ['fil'],
    targetCurrency: 'PHP',
    ogLocale: 'fil_PH',
    homeSeo: {
      title:
        'Magpadala ng pera sa Pilipinas nang mabilis at ligtas gamit ang Google FX rates',
      description:
        'Magpadala ng pera sa Pilipinas nang instant gamit ang real-time Google FX rates at walang nakatagong bayarin.Maranasan ang simple at secure na money transfer na may 24/7 na suporta.',
    },
  },
];

export const SUPPORTED_LANGUAGES: SupportedLanguage[] =
  CORRIDOR_I18N_CONFIG.map(({ code, label, flag }) => ({
    code,
    label,
    flag,
  }));

export const ISO3_TO_LANG = CORRIDOR_I18N_CONFIG.reduce<Record<string, string>>(
  (acc, config) => {
    config.routeSlugs.forEach((slug) => {
      acc[slug] = config.code;
    });
    return acc;
  },
  {},
);

export const COUNTRY_TO_ISO3_ROUTE: Record<string, string> = {
  VN: 'vi',
  PH: 'fil',
};

const LANGUAGE_CONFIG_BY_CODE = CORRIDOR_I18N_CONFIG.reduce<
  Record<string, CorridorI18nConfig>
>((acc, config) => {
  acc[config.code] = config;
  return acc;
}, {});

const ROUTE_SLUG_TO_CONFIG = CORRIDOR_I18N_CONFIG.reduce<
  Record<string, CorridorI18nConfig>
>((acc, config) => {
  config.routeSlugs.forEach((slug) => {
    acc[slug] = config;
  });
  return acc;
}, {});

export function getLanguageConfig(lang = DEFAULT_LANGUAGE): CorridorI18nConfig {
  return (
    LANGUAGE_CONFIG_BY_CODE[lang] ?? LANGUAGE_CONFIG_BY_CODE[DEFAULT_LANGUAGE]!
  );
}

export function getLanguageConfigFromPathname(
  pathname = '',
): CorridorI18nConfig {
  const firstSegment = pathname.trim().toLowerCase().split('/')[1] || '';
  return ROUTE_SLUG_TO_CONFIG[firstSegment] ?? getLanguageConfig();
}

export function getLanguageFromPathname(pathname = ''): string {
  return getLanguageConfigFromPathname(pathname).code;
}

export function getTargetCurrencyByLanguage(lang = DEFAULT_LANGUAGE): string {
  return getLanguageConfig(lang).targetCurrency;
}

export function getTargetCurrencyFromPathname(pathname = ''): string {
  return getLanguageConfigFromPathname(pathname).targetCurrency;
}

export function getRoutePrefixByLanguage(lang = DEFAULT_LANGUAGE): string {
  return getLanguageConfig(lang).routePrefix;
}

export function getLegacyRoutePrefixByLanguage(
  lang = DEFAULT_LANGUAGE,
): string {
  return getLanguageConfig(lang).legacyRoutePrefix ?? '';
}

export function getOgLocaleByLanguage(lang = DEFAULT_LANGUAGE): string {
  return getLanguageConfig(lang).ogLocale;
}

export function isLocalizedCorridorLanguage(lang = DEFAULT_LANGUAGE): boolean {
  return getTargetCurrencyByLanguage(lang) !== DEFAULT_TARGET_CURRENCY;
}

export function isLocalizedCorridorPathname(pathname = ''): boolean {
  return isLocalizedCorridorLanguage(getLanguageFromPathname(pathname));
}
